import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import type { FilterOption } from "@outpave/ds";
import { GET_USER_FILTER_OPTIONS } from "graphql/user";
import UserStatus from "enums/UserStatus";
import fullName from "utils/fullName";

interface UserFilterNode {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * Ceiling on the option list. The DS `SelectFilter` takes static options and
 * searches them locally, so it cannot page: a company past this many active
 * people gets a truncated list. Lifting it needs an async SelectFilter in the
 * package rather than a bigger number here.
 */
const MAX_OPTIONS = 200;

/**
 * People selectable in an employee filter, sorted by display name. Pass
 * `skip` from a page that may redirect, so the query never fires for someone
 * who will not see the filter.
 */
export const useEmployeeFilterOptions = ({
  skip = false,
}: { skip?: boolean } = {}): FilterOption[] => {
  const { data } = useQuery(GET_USER_FILTER_OPTIONS, {
    variables: {
      first: MAX_OPTIONS,
      status: [UserStatus.ACTIVE, UserStatus.PENDING],
    },
    skip,
  });

  return useMemo(() => {
    const edges: { node: UserFilterNode }[] = data?.users?.edges ?? [];
    return edges
      .map(({ node }) => ({ value: node.id, label: fullName(node) }))
      .filter((option) => option.label !== "")
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);
};

export default useEmployeeFilterOptions;
